import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { applyMiddleware, createStore } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly'
import thunk from 'redux-thunk'
import app from './reducers'
import { historyMiddleware } from './Middlewares/History.middleware'
import MiniCartContainer from './Containers/MiniCart.container'
import QuickSearchContainer from './Containers/QuickSearch.container'
import TopLinksContainer from './Containers/TopLinks.container'
import NavigationContainer from './Containers/Navigation.container'
import FacetedSearchContainer from './Containers/FacetedSearch.container'
import NotificationContainer from './Containers/Notification.container'
import InformationPopup from './Components/InformationPopup'
import ScrollHandler from './Components/ScrollHandler'
import DynamicComponent from './Components/DynamicComponent'
import VehicleFinderContainer from './Containers/VehicleFinder.container'

window.__litium = window.__litium || {}
const preloadState = window.__litium.preloadState || {}
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = (window.__litium.store =
    window.__litium.store ||
    (window.parent.__litium && window.parent.__litium.store) ||
    createStore(
        app,
        preloadState,
        composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
    ))
window.__litium = {
    ...window.__litium,
    bootstrapComponents: (excludedComponents = []) => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents(excludedComponents)
    },
    cache: {}, // for storing cache data for current request
}

const components = [
    {
        id: 'miniCart',
        Component: MiniCartContainer,
    },
    {
        id: 'quickSearch',
        Component: QuickSearchContainer,
    },
    {
        id: 'navbar',
        Component: NavigationContainer,
    },
    {
        id: 'facetedSearch',
        Component: FacetedSearchContainer,
    },
    {
        id: 'globalNotification',
        Component: NotificationContainer,
    },
    {
        id: 'myPagePersons',
        import: 'PersonList.container',
    },
    {
        id: 'myPageAddresses',
        import: 'AddressList.container',
    },
    {
        id: 'checkout',
        import: 'Checkout.container',
    },
    {
        id: 'tireHotel',
        import: 'TireHotel.container',
    },
    {
        id: 'tireHotelBookTireChange',
        import: 'TireHotelBookTireChange.container',
    },
    {
        id: 'tireHotelBookingsComponent',
        import: 'tireHotelBookingsComponent.container',
    },
    {
        id: 'stationFinder',
        import: 'StationFinder.container',
    },
    {
        id: 'customerSearchResultTable',
        import: 'CustomerSearchResult.container',
    },
]

const renderComponents = () => {
    components.forEach((item) => {
        const element = document.getElementById(item.id)
        if (!element) {
            return
        }
        const Component = item.import
            ? DynamicComponent({
                  loader: () => import('./Containers/' + item.import),
              })
            : item.Component
        ReactDOM.render(
            <Provider store={store}>
                <Component />
            </Provider>,
            element
        )
    })
}

const bootstrapComponents = (excludedComponents = []) => {
    renderComponents()

    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Containers/LightboxImages.container'),
        })
        const rootElement = document.getElementById('lightBoxImages')
        const productName = document.getElementById('productName').innerText
        const images = Array.from(rootElement.querySelectorAll('a')).map(
            (img) => ({
                src: img.dataset.src,
                alt: productName,
            })
        )
        const thumbnails = Array.from(
            rootElement.querySelectorAll('a img')
        ).map((img) => ({ src: img.src }))
        ReactDOM.render(
            <Provider store={store}>
                <LightboxImages
                    images={images}
                    thumbnails={thumbnails}
                    badgeImageUrl={rootElement.dataset.badgeImageUrl}
                    productName={productName}
                />
            </Provider>,
            document.getElementById('lightBoxImages')
        )
    }

    if (document.getElementById('googleReview')) {
        const element = document.getElementById('googleReview')
        const lat = element.getAttribute('data-lat')
        const long = element.getAttribute('data-long')
        const name = element.getAttribute('data-name')
        const GoogleReviewComponent = DynamicComponent({
            loader: () => import('./Components/GoogleReview'),
        })

        ReactDOM.render(
            <GoogleReviewComponent {...{ lat, long, name }} />,
            element
        )
    }

    if (document.getElementById('stationMap')) {
        const element = document.getElementById('stationMap')
        const lat = element.getAttribute('data-lat')
        const long = element.getAttribute('data-long')
        const color = element.getAttribute('data-color')
        const name = element.getAttribute('data-name')
        const address = element.getAttribute('data-address')
        const phone = element.getAttribute('data-phone')
        const StationMapComponent = DynamicComponent({
            loader: () => import('./Components/StationMap'),
        })

        ReactDOM.render(
            <StationMapComponent
                {...{ lat, long, color, name, address, phone }}
            />,
            element
        )
    }

    if (document.getElementById('directionsButton')) {
        const element = document.getElementById('directionsButton')
        const address = element.getAttribute('data-address')
        const DirectionsButtonComponent = DynamicComponent({
            loader: () => import('./Components/DirectionsButton'),
        })

        ReactDOM.render(<DirectionsButtonComponent {...{ address }} />, element)
    }

    if (document.getElementById('priceClassFilter')) {
        const PriceClassFilterContainer = DynamicComponent({
            loader: () => import('./Containers/PriceClassFilter.Container'),
        })

        ReactDOM.render(
            <Provider store={store}>
                <PriceClassFilterContainer />
            </Provider>,
            document.getElementById('priceClassFilter')
        )
    }

    if (document.getElementById('tirePressurePage')) {
        const TirePressurePageContainer = DynamicComponent({
            loader: () => import('./Containers/TirePressurePage.container'),
            loading: <div style={{ height: '100px', width: '100%' }}></div>,
        })

        ReactDOM.render(
            <Provider store={store}>
                <TirePressurePageContainer />
            </Provider>,
            document.getElementById('tirePressurePage')
        )
    }

    if (document.getElementById('thumbnailsWithLightbox')) {
        const ThumbnailsWithLightboxContainer = DynamicComponent({
            loader: () =>
                import('./Containers/ThumbnailsWithLightbox.container'),
        })
        const rootElement = document.getElementById('thumbnailsWithLightbox')
        const images = Array.from(rootElement.querySelectorAll('a')).map(
            (img) => ({
                src: img.dataset.src,
            })
        )
        const thumbnails = Array.from(
            rootElement.querySelectorAll('a img')
        ).map((img) => ({ src: img.src }))
        ReactDOM.render(
            <Provider store={store}>
                <ThumbnailsWithLightboxContainer
                    images={images}
                    thumbnails={thumbnails}
                />
            </Provider>,
            document.getElementById('thumbnailsWithLightbox')
        )
    }

    if (document.getElementById('topLinks')) {
        const links = window.__litium.topLinks
        if (links) {
            ReactDOM.render(
                <TopLinksContainer
                    values={{
                        links,
                        iconCssClass: 'fa fa-link',
                        menuLinkTitle: 'toplinks.title',
                        showOnlyTextIfSingleLink: true,
                    }}
                />,
                document.getElementById('topLinks')
            )
        }
    }

    if (document.getElementById('languageLinks')) {
        const links = window.__litium.languageLinks
        if (links) {
            ReactDOM.render(
                <TopLinksContainer
                    values={{
                        links,
                        iconCssClass: 'fa fa-globe',
                        menuLinkTitle: 'languagelinks.title',
                        showOnlyTextIfSingleLink: false,
                    }}
                />,
                document.getElementById('languageLinks')
            )
        }
    }

    if (document.getElementById('InformationPopup')) {
        const informationPopup = document.getElementById('InformationPopup')
        ReactDOM.render(
            <Provider store={store}>
                <InformationPopup
                    backgroundImageUrl={
                        informationPopup.dataset.backgroundImageUrl
                    }
                    informationText={informationPopup.dataset.informationText}
                    cookieExpiryTime={informationPopup.dataset.cookieExpiryTime}
                    cookieName="InformationPopup"
                />
            </Provider>,
            informationPopup
        )
    }

    if (document.getElementById('TireInformationExplination')) {
        const tireInformationExplination = document.getElementById(
            'TireInformationExplination'
        )
        const TireInfromationExplinationContainer = DynamicComponent({
            loader: () =>
                import('./Containers/TireInformationExplination.container'),
        })

        ReactDOM.render(
            <Provider store={store}>
                <TireInfromationExplinationContainer />
            </Provider>,
            tireInformationExplination
        )
    }

    if (document.getElementById('CarWashFinder')) {
        const carWashFinderContainer = document.getElementById('CarWashFinder')
        const CarWashFinderContainer = DynamicComponent({
            loader: () => import('./Containers/CarWashFinder.container'),
        })

        const { title, text } = carWashFinderContainer.dataset

        ReactDOM.render(
            <Provider store={store}>
                <CarWashFinderContainer title={title} text={text} />
            </Provider>,
            carWashFinderContainer
        )
    }

    if (document.getElementById('GlobalInformationPopup')) {
        const informationPopup = document.getElementById(
            'GlobalInformationPopup'
        )
        ReactDOM.render(
            <Provider store={store}>
                <InformationPopup
                    backgroundImageUrl={
                        window.__litium.informationPopupBackgroundImageUrl
                    }
                    informationText={window.__litium.informationPopupText}
                    cookieName="GlobalInformationPopup"
                />
            </Provider>,
            informationPopup
        )
    }

    if (document.getElementById('scrollHandler')) {
        const onBreakpoint = (isScrolled) => {
            const noScrollClass = 'header--transparent'
            const header = document.querySelector('#header')
            if (isScrolled) {
                header.className = header.className
                    .split(' ')
                    .reduce(
                        (acc, curr) =>
                            acc + (curr === noScrollClass ? '' : ' ' + curr)
                    )
            } else {
                header.className += ' ' + noScrollClass
            }
        }

        ReactDOM.render(
            <ScrollHandler onBreakpoint={onBreakpoint} breakpoint={50} />,
            document.getElementById('scrollHandler')
        )
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        })
        Array.from(document.querySelectorAll('.slider')).forEach((slider) => {
            const values = [...slider.querySelectorAll('.slider__block')].map(
                ({ dataset }) => {
                    return {
                        image: dataset.image,
                        url: dataset.url,
                        text: dataset.text,
                    }
                }
            )
            if (values.length > 0) {
                ReactDOM.render(<Slider values={values} />, slider)
            }
        })
    }

    if (document.querySelectorAll('.quantity-changer-placeholder').length > 0) {
        const QuantityChangerContainer = DynamicComponent({
            loader: () => import('./Containers/QuantityChanger.container'),
        })
        Array.from(
            document.querySelectorAll('.quantity-changer-placeholder')
        ).map((placeholder) => {
            const quantityFieldId = placeholder.dataset.fieldId
            const defaultQuantity = placeholder.dataset.defaultQuantity
            const maxQuantity = placeholder.dataset.maxQuantity
            if (quantityFieldId) {
                ReactDOM.render(
                    <QuantityChangerContainer
                        quantityFieldId={quantityFieldId}
                        defaultQuantity={defaultQuantity}
                        maxQuantity={maxQuantity}
                    />,
                    placeholder
                )
            }
        })
    }

    if (document.getElementById('VehicleFinder')) {
        var vehicleFinder = document.getElementById('VehicleFinder')
        // const VehicleFinderContainer = DynamicComponent({
        //     loader: () => import('./Containers/VehicleFinder.container'),
        // })
        const { dataset } = vehicleFinder
        ReactDOM.render(
            <Provider store={store}>
                <VehicleFinderContainer
                    {...dataset}
                    bannerImageUrl={dataset.bannerImage}
                />
            </Provider>,
            vehicleFinder
        )
    }

    const datePickers = document.querySelectorAll(
        '.my-tirehotel-info-panel-MyTireHotelDatePicker'
    )
    if (datePickers.length > 0) {
        const MyTireHotelDatePickerContainer = DynamicComponent({
            loader: () =>
                import('./Containers/MyTireHotelDatePicker.container'),
        })
        for (const datePicker of datePickers) {
            ReactDOM.render(
                <Provider store={store}>
                    <MyTireHotelDatePickerContainer {...datePicker.dataset} />
                </Provider>,
                datePicker
            )
        }
    }

    if (
        document.getElementById('facetedSearchCompact') &&
        !excludedComponents.includes('facetedSearchCompact')
    ) {
        const facetedSearchCompact = document.getElementById(
            'facetedSearchCompact'
        )
        const FacetedSearchCompactContainer = DynamicComponent({
            loader: () => import('./Containers/FacetedSearchCompact.container'),
            loading: <div style={{ height: '350px', width: '100%' }}></div>,
        })

        ReactDOM.render(
            <Provider store={store}>
                <FacetedSearchCompactContainer
                    {...facetedSearchCompact.dataset}
                />
            </Provider>,
            facetedSearchCompact
        )
    }

    if (document.getElementById('downloadOrdersToExcel')) {
        const downloadOrdersToExcel = document.getElementById(
            'downloadOrdersToExcel'
        )
        const DownloadOrdersToExcelContainer = DynamicComponent({
            loader: () =>
                import('./Containers/DownloadOrdersToExcel.container'),
        })

        ReactDOM.render(
            <Provider store={store}>
                <DownloadOrdersToExcelContainer />
            </Provider>,
            downloadOrdersToExcel
        )
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Containers/BuyButton.container'),
        })
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                ReactDOM.render(
                    <Provider store={store}>
                        <BuyButton
                            {...button.dataset}
                            label={button.innerText}
                        />
                    </Provider>,
                    button
                )
            }
        )
    }

    if (document.querySelectorAll('.tireHotelBookingsComponent'). length > 0) {
        const Bookings = DynamicComponent({
            loader: () => import('./Containers/tireHotelBookingsComponent.container'),
        })
        Array.from(document.querySelectorAll('.tireHotelBookingsComponent')).forEach((booking) => {
            console.log(booking.dataset);
            ReactDOM.render(
                <Provider store={store}>
                    <Bookings {...booking.dataset} />
                </Provider>,
                booking
            )
        })
    }

    if (document.querySelectorAll('.product-select-button').length > 0) {
        const SelectButton = DynamicComponent({
            loader: () => import('./Containers/ProductSelectButton.container'),
        })
        Array.from(document.querySelectorAll('.product-select-button')).forEach(
            (button) => {
                ReactDOM.render(
                    <Provider store={store}>
                        <SelectButton
                            {...button.dataset}
                            label={button.innerText}
                        />
                    </Provider>,
                    button
                )
            }
        )
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.container'),
        })
        Array.from(document.querySelectorAll('reorder-button')).forEach(
            (button) => {
                const { cssClass, orderId, title } = button.dataset
                const label = button.innerText
                ReactDOM.render(
                    <Provider store={store}>
                        <ReorderButton
                            {...{ label, title, cssClass, orderId }}
                        />
                    </Provider>,
                    button
                )
            }
        )
    }

    if (document.querySelectorAll('.searchable-dropdown').length > 0) {
        const SearchableDropdownContainer = DynamicComponent({
            loader: () => import('./Containers/SearchableDropdown.container'),
        })

        Array.from(document.querySelectorAll('.searchable-dropdown')).map(
            (searchableDropdown) => {
                const optionslist = Array.from(
                    searchableDropdown.querySelectorAll('div')
                ).map((option) => {
                    return {
                        key: option.dataset.text,
                        text: option.dataset.text,
                        value: option.dataset.value,
                        selected: option.dataset.selected === 'True',
                    }
                })

                if (optionslist.length > 0) {
                    ReactDOM.render(
                        <SearchableDropdownContainer options={optionslist} />,
                        searchableDropdown
                    )
                }
            }
        )
    }

    if (
        document.getElementById('completeWheels') &&
        !excludedComponents.includes('completeWheels')
    ) {
        const CompleteWheelsContainer = DynamicComponent({
            loader: () => import('./Containers/CompleteWheels.container'),
        })
        ReactDOM.render(
            <Provider store={store}>
                <CompleteWheelsContainer />
            </Provider>,
            document.getElementById('completeWheels')
        )
    }

    if (document.getElementById('sort-selector')) {
        const element = document.getElementById('sort-selector')
        const SortSelectorContainer = DynamicComponent({
            loader: () => import('./Containers/SortSelector.container'),
        })
        ReactDOM.render(
            <Provider store={store}>
                <SortSelectorContainer
                    selections={JSON.parse(element.dataset.sortSelections)}
                />
            </Provider>,
            element
        )
    }

    if (document.getElementById('pagination')) {
        const element = document.getElementById('pagination')
        const PaginationContainer = DynamicComponent({
            loader: () => import('./Containers/Pagination.container'),
        })
        ReactDOM.render(
            <Provider store={store}>
                <PaginationContainer
                    model={JSON.parse(element.dataset.model)}
                />
            </Provider>,
            element
        )
    }

    if (document.getElementById('product-skeleton-loader')) {
        const ProductSkeletonLoader = DynamicComponent({
            loader: () =>
                import('./Containers/ProductSekeltonLoader.container'),
        })
        const productList = document.getElementById('product-list-view')
        const skeletonLoader = document.getElementById(
            'product-skeleton-loader'
        )
        ReactDOM.render(
            <Provider store={store}>
                <ProductSkeletonLoader productList={productList} />
            </Provider>,
            skeletonLoader
        )
    }

    if (document.getElementById('showAllButton')) {
        const ShowAllButton = DynamicComponent({
            loader: () => import('./Containers/ShowAllButton.container'),
        })
        const showAllButton = document.getElementById('showAllButton')

        ReactDOM.render(
            <Provider store={store}>
                <ShowAllButton {...showAllButton.dataset} />
            </Provider>,
            showAllButton
        )
    }

    if (document.getElementById('productFitter')) {
        const ProductFitterContainer = DynamicComponent({
            loader: () => import('./Containers/ProductFitter.container'),
        })
        const productFitter = document.getElementById('productFitter')

        ReactDOM.render(
            <Provider store={store}>
                <ProductFitterContainer {...productFitter.dataset} />
            </Provider>,
            productFitter
        )
    }
}

bootstrapComponents()
